import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Building2, Star, ClipboardCheck, RefreshCw, Trophy, ArrowRight, Sparkles } from "lucide-react";
import { HERO_IMAGE, STATES, LISTINGS, getStateUrl, getCityUrl, getStateName, stateByCode } from "../constants";
import {
  getContent,
  getCourses,
  getPopularCities,
  getCoursesSync,
  getPopularCitiesSync,
  getLocalContent,
} from "../api";
import { FeaturedCard, TopRatedCard } from "../components/CourseCard";
import ShareSection from "../components/ShareSection";
import FormattedText from "../components/FormattedText";
import SEO from "../components/SEO";
import { buildWebsiteSchema, buildOrganizationSchema } from "../utils/seoSchemas";

const statIcons = [MapPin, ClipboardCheck, RefreshCw];

const Home = () => {
  const [search, setSearch] = useState("");
  const [c, setContent] = useState(() => getLocalContent());
  const [allCourses, setAllCourses] = useState(() => getCoursesSync());
  const [featured, setFeatured] = useState(() => getCoursesSync({ featured: true }));
  const [topRated, setTopRated] = useState(() => getCoursesSync({ sort: "top", limit: 8 }));
  const [popular, setPopular] = useState(() => getPopularCitiesSync());
  const navigate = useNavigate();

  useEffect(() => {
    getContent().then((data) => { if (data) setContent(data); }).catch(() => {});
    getCourses().then((data) => {
      if (data?.length) {
        setAllCourses(data);
        const feat = data.filter((x) => x.featured);
        if (feat.length) setFeatured(feat);
        const top = [...data].sort((a, b) => (b.rating || 0) - (a.rating || 0) || (b.reviewCount || 0) - (a.reviewCount || 0)).slice(0, 8);
        setTopRated(top);
      }
    }).catch(() => {});
    getPopularCities().then((data) => { if (data?.length) setPopular(data); }).catch(() => {});
  }, []);

  const totalCitiesCount = React.useMemo(() => {
    const citiesSet = new Set();
    allCourses.forEach((cr) => {
      if (cr.city && cr.state) {
        citiesSet.add(`${cr.city.toLowerCase()}:${cr.state.toLowerCase()}`);
      }
    });
    return citiesSet.size;
  }, [allCourses]);

  const stateCounts = React.useMemo(() => {
    const counts = {};
    allCourses.forEach((cr) => {
      const code = (cr.state || "").toLowerCase();
      counts[code] = (counts[code] || 0) + 1;
    });
    return counts;
  }, [allCourses]);

  const handleFind = (e) => {
    e.preventDefault();
    const q = search.trim().toLowerCase();
    if (!q) return navigate("/state/alabama");
    const st = stateByCode(q);
    if (st) return navigate(getStateUrl(st.code));
    const foundListing = LISTINGS.find(
      (l) =>
        l.cityName.toLowerCase() === q ||
        l.citySlug.toLowerCase() === q ||
        l.slug.toLowerCase() === q
    );
    if (foundListing) return navigate(foundListing.path);
    navigate(`/state/alabama?q=${encodeURIComponent(search.trim())}`);
  };

  const homeSchemas = [buildWebsiteSchema(), buildOrganizationSchema()];

  return (
    <div data-testid="home-page" className="bg-[#f8fafc]">
      <SEO
        title={c.heroTitle ? `Mini Golf Alabama | ${c.heroTitle}` : "Mini Golf Alabama | Best Indoor & Outdoor Mini Golf Near Me"}
        description={c.heroSubtitle || "Find a fun and exciting mini golf putt putt course near me in Alabama for family outings, date nights, and friendly competition. Enjoy creative obstacles and challenging holes."}
        canonicalUrl="https://minigolfalabama.com"
        ogType="website"
        schema={homeSchemas}
      />
      {/* ================= HERO ================= */}
      <section className="relative min-h-[560px] flex items-center bg-[#111217]">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="Mini golf putt putt course in Alabama"
            className="w-full h-full object-cover opacity-35 filter saturate-125"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111217] via-[#111217]/90 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111217] via-transparent to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-3xl">
            {/* Tag Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-white border border-white/15 backdrop-blur-md mb-6">
              <span className="w-2 h-2 rounded-full bg-[#e5243b] animate-pulse"></span>
              {allCourses.length > 0
                ? `${allCourses.length}+ Verified Mini Golf Courses · ${totalCitiesCount}+ Cities`
                : "America's Premier Mini Golf Directory · All 50 States"}
            </div>

            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] text-white" data-testid="hero-title">
              <span>{c?.heroTitle1 || "Discover Great Places"}</span>
              <br />
              <span className="bg-gradient-to-r from-[#e5243b] via-[#ff4d6d] to-[#3b82f6] bg-clip-text text-transparent">
                {c?.heroTitle2 || "In Mini Golf Putt Putt"}
              </span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-gray-300 max-w-2xl leading-relaxed">
              {c?.heroSubtitle || "Find awesome Mini Golf Putt Putt Places, indoor courses, family fun parks and activities in Alabama and across America."}
            </p>

            <form onSubmit={handleFind} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Enter state or city (e.g. Alabama, Birmingham, Florida)..."
                  data-testid="hero-search-input"
                  className="w-full pl-12 pr-4 py-4 rounded-xl bg-white text-gray-900 placeholder-gray-500 text-sm font-medium shadow-lg focus:outline-none focus:ring-2 focus:ring-[#e5243b]"
                />
              </div>
              <button
                type="submit"
                data-testid="hero-search-button"
                className="px-8 py-4 rounded-xl bg-[#e5243b] hover:bg-[#cf1c31] text-white text-sm font-bold shadow-lg shadow-red-900/40 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                <span>Find Places</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            {/* Quick Action & Searches */}
            <div className="flex items-center gap-3 mt-6 text-xs text-gray-300 flex-wrap">
              <Link
                to="/courses"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#e5243b] text-white font-bold shadow-md hover:bg-[#cf1c31] transition-all"
              >
                <span>{allCourses.length > 0 ? `Browse All ${allCourses.length}+ Courses` : "Browse Courses Directory"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link
                to="/states"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/15 backdrop-blur-sm transition-all"
              >
                All 50 States
              </Link>
              <span className="font-semibold text-gray-400 uppercase tracking-wider ml-1">Top Searches:</span>
              <Link to="/state/alabama" className="hover:text-white underline underline-offset-4 decoration-[#e5243b]">
                Alabama Courses
              </Link>
              <Link to={getCityUrl("al", "Athens")} className="hover:text-white underline underline-offset-4 decoration-blue-500">
                Athens
              </Link>
              <Link to={getCityUrl("al", "Birmingham")} className="hover:text-white underline underline-offset-4 decoration-blue-500">
                Birmingham
              </Link>
              <Link to={getCityUrl("al", "Huntsville")} className="hover:text-white underline underline-offset-4 decoration-blue-500">
                Huntsville
              </Link>
              <Link to="/top-rated" className="hover:text-white underline underline-offset-4 decoration-[#f5cd79]">
                Top 50 Rated
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ================= ALABAMA SPOTLIGHT ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
        <div className="bg-gradient-to-r from-[#181a20] to-[#252836] border border-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 text-white">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-[#e5243b] flex items-center justify-center flex-shrink-0 shadow-lg shadow-red-900/50">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">
                {c?.spotlightTitle || "Featured Spotlight: Mini Golf in Alabama"}
              </h3>
              <p className="text-sm text-gray-300 mt-1">
                {c?.spotlightSubtitle || "Explore popular indoor and outdoor putt putt courses from coastal greens in Gulf Shores to Birmingham glow courses."}
              </p>
            </div>
          </div>
          <Link
            to={c?.spotlightButtonLink || "/state/alabama"}
            className="whitespace-nowrap px-6 py-3 rounded-xl bg-white text-gray-900 font-bold text-sm hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            <span>{c?.spotlightButtonText || "View Alabama Courses"}</span>
            <ArrowRight className="w-4 h-4 text-[#e5243b]" />
          </Link>
        </div>
      </section>

      {/* ================= INTRO SEO CONTENT ================= */}
      {c && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              {c.introTitle}
            </h2>
            <div className="w-20 h-1.5 bg-[#e5243b] rounded-full mx-auto mt-4"></div>
          </div>
          <div className="space-y-5 bg-white p-8 sm:p-10 rounded-2xl border border-gray-200 shadow-sm">
            {c.introParagraphs.map((p, i) => (
              <FormattedText
                key={i}
                text={p}
                paragraphClassName="text-[15px] leading-relaxed text-gray-700"
                linkClassName="text-[#e5243b] underline font-semibold hover:text-[#cf1c31]"
              />
            ))}
          </div>
        </section>
      )}

      {/* ================= FEATURED COURSES ================= */}
      {featured && featured.length > 0 && (
        <section className="bg-white py-16 border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#e5243b]/10 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-[#e5243b]" />
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight" data-testid="featured-heading">
                    Featured Courses
                  </h2>
                  <p className="text-sm text-gray-500">Handpicked premier miniature golf destinations</p>
                </div>
              </div>
              <Link
                to="/courses"
                className="text-sm font-bold text-[#e5243b] hover:text-[#cf1c31] flex items-center gap-1"
              >
                <span>Explore All ({allCourses.length})</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-grid">
              {featured.map((course) => (
                <FeaturedCard key={course.id} course={course} />
              ))}
            </div>

            {c && c.stats && c.stats.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-14">
                {c.stats.map((s, i) => {
                  const Icon = statIcons[i % statIcons.length];
                  return (
                    <div key={i} className="bg-[#f8fafc] rounded-2xl border border-gray-200 p-6 text-center hover:border-red-200 hover:shadow-md transition-all">
                      <div className="w-12 h-12 mx-auto rounded-xl bg-red-50 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-[#e5243b]" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">{s.title}</h3>
                      <p className="text-sm text-gray-600 mt-2">{s.text}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ================= WHY CHOOSE ================= */}
      {c && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              {c.whyTitle}
            </h2>
            <div className="w-20 h-1.5 bg-[#2563eb] rounded-full mx-auto mt-4"></div>
          </div>
          <div className="space-y-5 bg-white p-8 sm:p-10 rounded-2xl border border-gray-200 shadow-sm">
            {c.whyParagraphs.map((p, i) => (
              <FormattedText
                key={i}
                text={p}
                paragraphClassName="text-[15px] leading-relaxed text-gray-700"
                linkClassName="text-[#2563eb] underline font-semibold hover:text-blue-700"
              />
            ))}
          </div>
        </section>
      )}

      {/* ================= POPULAR CITIES ================= */}
      {popular && popular.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-[#2563eb]" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                Popular Cities for Mini Golf
              </h2>
              <p className="text-sm text-gray-500">Top destinations with the highest number of top-rated courses</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="popular-cities-grid">
            {popular.map((pc) => (
              <Link
                key={`${pc.state}-${pc.slug}`}
                to={getCityUrl(pc.state, pc.slug)}
                className="block bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-md transition-all group"
              >
                <div className="font-bold text-gray-900 group-hover:text-[#2563eb] transition-colors">
                  {pc.city}, {getStateName(pc.state)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {pc.courses} courses · <span className="text-amber-500 font-medium">★ {pc.avgRating}</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <Link
              to="/courses"
              className="px-6 py-3 text-sm font-bold text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-xl shadow-md transition-all"
            >
              Explore All {allCourses.length}+ Courses
            </Link>
            <Link
              to="/states"
              className="px-6 py-3 text-sm font-bold text-gray-900 border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              Browse All {totalCitiesCount}+ Cities in 50 States
            </Link>
          </div>
        </section>
      )}

      {/* ================= BROWSE BY STATE ================= */}
      <section className="bg-white py-16 border-t border-gray-200" data-testid="browse-states-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#e5243b]" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                  Browse Courses by State
                </h2>
                <p className="text-sm text-gray-500">Find mini golf locations in Alabama and across all 50 states</p>
              </div>
            </div>
            <Link
              to="/states"
              className="text-sm font-bold text-[#e5243b] hover:text-[#cf1c31] flex items-center gap-1"
            >
              <span>View All 50 States</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="states-grid">
            {STATES.map((s) => {
              const isAlabama = s.code === "al";
              const count = stateCounts[s.code] || 0;
              return (
                <Link
                  key={s.code}
                  to={getStateUrl(s.code)}
                  data-testid={`state-link-${s.code}`}
                  className={`rounded-xl px-3.5 py-3 text-xs sm:text-sm font-semibold flex items-center justify-between transition-all ${
                    isAlabama
                      ? "bg-[#e5243b] text-white shadow-md shadow-red-900/20 hover:bg-[#cf1c31]"
                      : "bg-[#f8fafc] border border-gray-200 text-gray-700 hover:border-red-300 hover:text-[#e5243b] hover:shadow-sm"
                  }`}
                >
                  <span className="truncate">{s.name} {isAlabama && "⭐"}</span>
                  {count > 0 && (
                    <span
                      className={`ml-1.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${
                        isAlabama ? "bg-white/25 text-white" : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= TOP RATED ================= */}
      {topRated && topRated.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                  Top Rated Mini Golf Courses
                </h2>
                <p className="text-sm text-gray-500">Highest rated putt putt venues based on visitor reviews</p>
              </div>
            </div>
            <Link
              to="/courses"
              className="text-sm font-bold text-[#e5243b] hover:text-[#cf1c31] transition-colors flex items-center gap-1"
            >
              <span>View All {allCourses.length} Courses</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="top-rated-grid">
            {topRated.map((course) => (
              <TopRatedCard key={course.id} course={course} />
            ))}
          </div>
        </section>
      )}

      <ShareSection content={c} />
    </div>
  );
};

export default Home;
